import React, { useState } from 'react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import { usePermissions } from '../../hooks/usePermissions';
import type { AaiNotification, OrbitAsk } from '../../types/aai';
import { Action, Chip, Divided, ListRow, Panel, PanelHead, RouteMark } from '../../components/aa';
import { usePeople } from './useOrbitRoom';
import { originLine, plural, scopeName, shortName, statusChip } from './roomCopy';
import { AskDrawer } from './RoomDrawers';
import type { AskMode } from './RoomDrawers';

/*
 * What the Room needs from this person, on their Home (and, for the Owner, on
 * the Controls tab). Two things: the onboarding questions still open on them,
 * and the questions someone handed to them. Nothing else moves; the Room itself
 * lives at the top of Company Context, which is also where the Controls variant
 * of this panel renders, so neither one offers a link to it.
 */

export const RoomHomePanel: React.FC<{
  variant: 'owner' | 'member' | 'controls';
  className?: string;
}> = ({ variant, className = '' }) => {
  const { can } = usePermissions();
  const mine = useAsync<{ asks: OrbitAsk[]; onboarding_left: number }>(() => aaiApi.myOrbitAsks(), 'Could not load your Orbit questions.');
  const people = usePeople(Boolean(mine.data));
  const connections = useAsync<AaiNotification[]>(async () => {
    if (variant !== 'controls') return { data: [] };
    const res = await aaiApi.listNotifications({ unread_only: true, limit: 50 });
    return { data: res.data.notifications.filter((n) => n.kind === 'connection_requested') };
  }, 'Could not load connection requests.', [variant]);
  const settle = async (n: AaiNotification) => {
    await aaiApi.markNotificationsRead([n.id]);
    connections.reload();
  };
  const [open, setOpen] = useState<{ ask: OrbitAsk; mode: AskMode } | null>(null);
  const me = (localStorage.getItem('user_email') || '').toLowerCase();

  if (mine.loading || mine.error || !mine.data) return null;
  const pendingConnections = connections.data || [];
  // Problem scoping is its own step, not onboarding: its questions do not count here.
  const asks = mine.data.asks.filter((a) => !a.later && a.purpose !== 'problem');
  const handed = asks.filter((a) => !a.onboarding || variant === 'controls');
  if (asks.length === 0 && pendingConnections.length === 0) return null;

  const canDelegate = can('aai_room_delegate') && variant !== 'member';
  const reload = () => mine.reload();

  // Parked: the "Orbit needs you, your onboarding is not finished" banner on
  // the Owner, head and employee homes. Their intake sections on the
  // dashboard are where the questions left are answered.
  // let read = 'the website and the documents on file';
  // if (variant === 'member') read = 'your role and department';
  // const continueCard = variant !== 'controls' && (
  //   <Banner
  //     className="mb-5"
  //     size="lg"
  //     mark={<span className="grid h-[46px] w-[46px] shrink-0 place-items-center rounded-tile bg-ink text-white"><Sparkles size={22} strokeWidth={2} /></span>}
  //     kicker="Orbit needs you"
  //     title={`Your onboarding is not finished: ${plural(asks.length, 'question', 'questions')} left`}
  //     desc={`Orbit already read ${read} and asks only what it could not answer itself. A few minutes, one card at a time. Skip anything; it waits for you.`}
  //     action={<Action arrow to="/orbit/deck">Continue onboarding</Action>}
  //   />
  // );
  if (variant !== 'controls') return null;

  // Not "Routed to you": Controls carries a panel of control actions under that
  // exact kicker, and these two now sit on the same tab.
  const kicker = 'Handed to you';
  const title = `${plural(handed.length, 'information request', 'information requests')}`;
  const desc = 'Questions handed to you by your Solution Partner, or raised by someone\'s onboarding. Answer here, or hand them down.';

  const list = handed.length > 0 && (
    <Panel className={`mb-5 ${className}`}>
      <PanelHead kicker={kicker} title={title} desc={desc} />
      <Divided>
        {handed.map((a) => {
          const chip = statusChip(a, people);
          let from = originLine(a, people);
          if (a.chain.length > 0) from = `From ${shortName(a.chain[a.chain.length - 1].from, people)}`;
          if (a.purpose === 'department' && a.scope) from = `${from} · ${scopeName(a.scope)}`;
          const note = a.chain.length > 0 && a.chain[a.chain.length - 1].note;
          const handWord = 'Hand down';
          return (
            <ListRow
              key={a.id}
              mark={<RouteMark glyph="Q" size="lg" className="bg-grape-soft text-[#6F3AA3]" />}
              kicker={from}
              title={a.question}
              badge={<Chip tone={chip.tone} dot>{chip.label}</Chip>}
              desc={note || a.why || undefined}
              right={(
                <div className="flex flex-wrap gap-1.5">
                  <Action size="sm" onClick={() => setOpen({ ask: a, mode: 'answer' })}>Answer</Action>
                  {canDelegate && <Action size="sm" variant="quiet" onClick={() => setOpen({ ask: a, mode: 'delegate' })}>{handWord}</Action>}
                </div>
              )}
            />
          );
        })}
      </Divided>
    </Panel>
  );

  const connectionList = pendingConnections.length > 0 && (
    <Panel className={`mb-5 ${className}`}>
      <PanelHead kicker="Connection requests" title={plural(pendingConnections.length, 'tool named by a department', 'tools named by departments')} desc="A department said it uses a tool that is not connected for the company yet. Connect it under Connectors below, then mark it handled." />
      <Divided>
        {pendingConnections.map((n) => (
          <ListRow
            key={n.id}
            mark={<RouteMark glyph="T" size="lg" className="bg-amber-50 text-amber-700" />}
            kicker="Not in the stack yet"
            title={n.title}
            desc={n.body || undefined}
            right={<Action size="sm" variant="quiet" onClick={() => settle(n)}>Mark handled</Action>}
          />
        ))}
      </Divided>
    </Panel>
  );

  return (
    <>
      {connectionList}
      {list}
      {open && (
        <AskDrawer
          ask={open.ask} mode={open.mode} me={me} people={people}
          canAnswer={can('aai_room_answer')} canDelegate={canDelegate} canEdit={false} isPartner={false}
          onClose={() => setOpen(null)} onChanged={reload}
        />
      )}
    </>
  );
};

export default RoomHomePanel;
